'use client';

import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { forwardRef } from 'react';

import { cn } from '../utils/cn';

interface ScrollBarProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> {
  /** Used to force mounting when more control is needed. Useful when controlling animation with React animation libraries. */
  forceMount?: true | undefined;
  orientation?: 'vertical' | 'horizontal';
  theme?: 'dark' | 'light';
}

const ScrollBar = forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ScrollBarProps
>(({ className, orientation = 'vertical', theme = 'light', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'absolute z-10 flex touch-none select-none bg-auto transition-opacity',
      orientation === 'vertical' && 'h-full w-4 pb-1 pt-1',
      orientation === 'horizontal' && 'h-4 w-full flex-col pl-1 pr-1',
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb
      className={cn(
        'relative w-1 flex-1 scroll-smooth rounded-full bg-opacity-25 transition-opacity hover:bg-opacity-50',
        {
          'bg-white': theme === 'dark',
          'bg-black': theme === 'light',
        },
        {
          'ml-[5px] mr-[5px] hover:ml-1 hover:mr-1': orientation === 'vertical',
          'mb-[5px] mt-[5px] hover:mb-1 hover:mt-1': orientation === 'horizontal',
        },
      )}
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));

export interface ScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  theme?: 'dark' | 'light';
  viewportRef?: React.RefObject<HTMLDivElement>;
}

/**
 * A scrollable region with custom, cross-browser styling.
 * @see {@link https://stylus.design/scroll-area}
 */
export const ScrollArea = forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, children, theme = 'light', viewportRef, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    data-testid='scroll-area'
    className={cn(
      'relative',
      {
        'bg-white': theme === 'light',
        'bg-slate-800 text-white': theme === 'dark',
      },
      className,
    )}
    {...props}
  >
    {/* Force display: block to fix a Radix bug https://github.com/radix-ui/primitives/issues/2722 */}
    <ScrollAreaPrimitive.Viewport
      ref={viewportRef}
      className='h-full w-full rounded-[inherit] [&>div]:!block'
    >
      {children}
    </ScrollAreaPrimitive.Viewport>
    {/** Two ScrollBars are required to enable the scrollbar for both vertical and horizontal scrolling.
     * https://www.radix-ui.com/primitives/docs/components/scroll-area#scrollbar */}
    <ScrollBar theme={theme} orientation='vertical' />
    <ScrollBar theme={theme} orientation='horizontal' />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
